/**
 * Connect's charts (2026-10-20): a chart that show_chart made (API classes/Assistant/Tools/ShowChartTool.php),
 * drawn as one SVG string. The chat (components/Dashboard/AssistantChart.vue) and the PDF report
 * (utils/assistantDocs.js) draw from this same code, so a report shows what the chat showed.
 *
 *   chart: { title, type: bar|line|donut|table, unit: money|count|percent|number, dims: [first breakdown, …],
 *            labels: [], series: [{ name, values: [number|null] }], table: { columns, rows, total },
 *            period: { from, to }, stores, note, compared_with }
 *
 * Rules kept from AreaChart (the dataviz notes): one value axis; grid and axes stay quiet; more than one
 * series carries a legend and, on a line, a dash of its own, so colour is never the only clue; a single
 * series says its values on the bars or at the end of the line; a line is monotone, so it never invents a
 * dip or a peak between two points; a day with no value is a gap, not a zero.
 *
 * Colours: with no palette the marks carry class names (arc-f-<role> fill, arc-k-<role> stroke) that the
 * chat's stylesheet colours from the theme, so dark mode follows; the PDF passes PRINT_PALETTE and gets the
 * colours written in. Every text that comes from data is escaped: the string goes into v-html.
 */

export const TIME_AXES = ["day", "week", "month", "hour", "weekday"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// 2026-10-28: a chart or file in Turkish (language "tr", from a Turkish conversation) has its dates and the words
// around its figures in Turkish, the way the API writes its cells ("1 – 4 Oca 2026"); English stays as it was.
const TR_MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
export const TR_MONTH_NAMES = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const TR_WEEKDAYS = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const SERIES = ["s0", "s1", "s2", "s3", "s4", "s5"];
// A line of its own for every series after the first, so print in grey and colour blindness still tell them apart.
const DASHES = ["", "6 4", "2 3", "9 3 2 3", "4 4", "1 3"];

/** The light theme's chart colours, written into the SVG for the PDF (white paper). */
export const PRINT_PALETTE = {
  s0: "#f97316", s1: "#2563eb", s2: "#059669", s3: "#9333ea", s4: "#0891b2", s5: "#be185d",
  other: "#94a3b8", grid: "#e2e8f0", zero: "#94a3b8", axis: "#5b6678", text: "#0f172a", bg: "#ffffff",
};

// ------------------------------------------------------------------ words and numbers

export function esc(s) {
  return String(s === null || s === undefined ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

const trimZero = (s) => s.replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
const locale = (n, digits) => n.toLocaleString("en-CA", { minimumFractionDigits: digits, maximumFractionDigits: digits });

/**
 * A value as people read it. how: "full" (tables, tooltips: $1,234.56), "bar" (on a bar: $1,235 from a
 * thousand up), "axis" (the value axis: $1.2k).
 */
export function formatValue(v, unit, how = "full") {
  if (v === null || v === undefined || v === "") return "–";
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  const minus = n < 0 ? "-" : "";
  const a = Math.abs(n);
  const short = (x) => (x >= 1e6 ? trimZero((x / 1e6).toFixed(1)) + "M" : x >= 1000 ? trimZero((x / 1000).toFixed(x >= 10000 ? 0 : 1)) + "k" : null);
  if (unit === "money") {
    if (how === "axis") return minus + "$" + (short(a) || String(Math.round(a)));
    if (how === "bar" && a >= 1000) return minus + "$" + locale(Math.round(a), 0);
    return minus + "$" + locale(a, 2);
  }
  if (unit === "percent") return minus + trimZero(a.toFixed(how === "axis" ? 0 : 1)) + "%";
  if (how === "axis") return minus + (short(a) || trimZero(a.toFixed(a < 10 && a % 1 ? 1 : 0)));
  if (unit === "count") return minus + locale(Math.round(a), 0);
  return minus + (Number.isInteger(a) ? locale(a, 0) : locale(a, 2));
}

function ymd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s));
  return m ? { y: +m[1], m: +m[2], d: +m[3], wd: new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay() } : null;
}

/**
 * A label along the chart: days and weeks come as 2026-10-14, months as 2026-10. long: for tables and tooltips.
 * lang "tr": in Turkish ("14 Eki", "Sal, 14 Eki 2026", the week as its days, "Ekim 2026").
 */
export function axisLabel(label, axis, long = false, lang) {
  const s = String(label === null || label === undefined ? "" : label);
  if (lang === "tr") return trAxisLabel(s, axis, long);
  if (axis === "day" || axis === "week") {
    const d = ymd(s);
    if (!d) return s;
    const text = MONTHS[d.m - 1] + " " + d.d;
    if (axis === "week") return long ? "Week of " + text + ", " + d.y : text;
    return long ? WEEKDAYS[d.wd] + " " + text + ", " + d.y : text;
  }
  if (axis === "month") {
    const m = /^(\d{4})-(\d{2})$/.exec(s);
    return m ? MONTHS[+m[2] - 1] + " " + m[1] : s;
  }
  return s;
}

function trAxisLabel(s, axis, long) {
  if (axis === "day" || axis === "week") {
    const d = ymd(s);
    if (!d) return s;
    const text = d.d + " " + TR_MONTHS[d.m - 1];
    if (!long) return text;
    if (axis === "week") return periodText({ from: s, to: new Date(Date.UTC(d.y, d.m - 1, d.d + 6)).toISOString().slice(0, 10) }, "tr");
    return TR_WEEKDAYS[d.wd] + ", " + text + " " + d.y;
  }
  if (axis === "month") {
    const m = /^(\d{4})-(\d{2})$/.exec(s);
    return m ? TR_MONTH_NAMES[+m[2] - 1] + " " + m[1] : s;
  }
  return s;
}

/** "Oct 8 – 14, 2026", "Sep 28 – Oct 4, 2026", "Oct 14, 2026" (lang "tr": "8 – 14 Eki 2026", "28 Eyl – 4 Eki 2026"). */
export function periodText(p, lang) {
  const a = p && ymd(p.from), b = p && ymd(p.to);
  if (!a || !b) return "";
  if (lang === "tr") {
    const mo = (x) => TR_MONTHS[x.m - 1];
    if (p.from === p.to) return a.d + " " + mo(a) + " " + a.y;
    if (a.y === b.y && a.m === b.m) return a.d + " – " + b.d + " " + mo(b) + " " + b.y;
    if (a.y === b.y) return a.d + " " + mo(a) + " – " + b.d + " " + mo(b) + " " + b.y;
    return a.d + " " + mo(a) + " " + a.y + " – " + b.d + " " + mo(b) + " " + b.y;
  }
  if (p.from === p.to) return MONTHS[a.m - 1] + " " + a.d + ", " + a.y;
  if (a.y === b.y && a.m === b.m) return MONTHS[a.m - 1] + " " + a.d + " – " + b.d + ", " + a.y;
  if (a.y === b.y) return MONTHS[a.m - 1] + " " + a.d + " – " + MONTHS[b.m - 1] + " " + b.d + ", " + a.y;
  return MONTHS[a.m - 1] + " " + a.d + ", " + a.y + " – " + MONTHS[b.m - 1] + " " + b.d + ", " + b.y;
}

/** The line under the title: the dates and the stores. lang: the file's language when the chart has none. */
export function chartSubtitle(chart, lang) {
  return [periodText(chart.period, chart.language || lang), chart.stores || ""].filter(Boolean).join(" · ");
}

/** "Against Oct 1 – 7: Net sales +12.3%, Orders −4.1%" when the chart was compared with an earlier period. */
export function compareText(chart, lang) {
  const c = chart.compared_with;
  if (!c || !c.change_pct) return "";
  const names = {};
  ((chart.table && chart.table.columns) || []).forEach((col) => { names[col.key] = col.label; });
  const parts = Object.keys(c.change_pct)
    .filter((m) => !/^(marketplace|all)_/.test(m))
    .slice(0, 3)
    .map((m) => { const p = Number(c.change_pct[m]); return (names[m] || m) + " " + (p > 0 ? "+" : p < 0 ? "−" : "") + trimZero(Math.abs(p).toFixed(1)) + "%"; });
  if (!parts.length) return "";
  return (chart.language || lang) === "tr"
    ? periodText(c, "tr") + " ile karşılaştırıldığında: " + parts.join(", ")
    : "Against " + periodText(c) + ": " + parts.join(", ");
}

/** One table cell as text: dates spelled out, figures in their unit. */
export function cellText(value, column, lang) {
  if (value === null || value === undefined || value === "") return "–";
  if (column.unit === "text") return TIME_AXES.indexOf(column.key) !== -1 ? axisLabel(value, column.key, true, lang) : String(value);
  return formatValue(value, column.unit, "full");
}

/**
 * A table of totals only (one row, no breakdown) reads better as a list, one figure a line, than as a row
 * of a dozen narrow columns: [{ label, unit, value, text }], or null for any other table.
 */
export function figureList(table, lang) {
  if (!table || !table.rows || table.rows.length !== 1 || table.total || !table.columns || !table.columns.length) return null;
  if (table.columns.some((c) => c.unit === "text")) return null;
  return table.columns.map((c, i) => ({ label: c.label, unit: c.unit, value: table.rows[0][i], text: cellText(table.rows[0][i], c, lang) }));
}

export function hasData(chart) {
  return !!(chart && chart.labels && chart.labels.length && chart.series && chart.series.some((s) => s.values.some((v) => v !== null && Number(v) !== 0)));
}

// ------------------------------------------------------------------ drawing helpers

// Text is not measured (the PDF has no DOM): each glyph is guessed from its kind, in ems of a sans-serif.
function glyph(ch) {
  if (/[ijlIft.,:;|!'()[\]{}/\- ]/.test(ch)) return 0.3;
  if (/[mwMW@%]/.test(ch)) return 0.86;
  if (/[A-Z]/.test(ch)) return 0.66;
  if (/[0-9$#*+=?]/.test(ch)) return 0.57;
  return 0.53;
}
const textW = (s, size) => { let w = 0; for (const ch of String(s)) w += glyph(ch); return w * size; };

function fit(s, width, size) {
  const t = String(s);
  if (textW(t, size) <= width) return t;
  let out = "";
  for (const ch of t) { if (textW(out + ch + "…", size) > width) break; out += ch; }
  return (out.trimEnd() || t.charAt(0)) + "…";
}

const r1 = (n) => Math.round(n * 10) / 10;

/** fill and stroke roles as class names (the chat) or as colours (a palette: the PDF). */
function painter(palette) {
  return (fill, stroke) => {
    if (palette) return ' fill="' + (fill ? palette[fill] : "none") + '"' + (stroke ? ' stroke="' + palette[stroke] + '"' : "");
    const cls = [];
    if (fill) cls.push("arc-f-" + fill);
    if (stroke) cls.push("arc-k-" + stroke);
    return ' class="' + cls.join(" ") + '"' + (fill ? "" : ' fill="none"');
  };
}

function seriesRole(s, i) {
  return /^Other( \(\d+\))?$/.test(s.name) ? "other" : SERIES[i % SERIES.length];
}

/** A round step for an axis: 1, 2, 2.5 or 5 times a power of ten. */
function niceStep(range, count, whole) {
  const raw = range / Math.max(1, count);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  return whole ? Math.max(1, Math.round(step)) : step;
}

/** The value axis: from zero (or the lowest value below it) to a round number above the highest. */
function scaleOf(values, count, unit) {
  const nums = values.filter((v) => v !== null && v !== undefined && isFinite(v)).map(Number);
  let lo = Math.min(0, ...nums), hi = Math.max(0, ...nums);
  if (lo === hi) hi = lo + 1;
  const step = niceStep(hi - lo, count, unit === "count");
  lo = Math.floor(lo / step + 1e-9) * step;
  hi = Math.ceil(hi / step - 1e-9) * step;
  const ticks = [];
  for (let v = lo; v <= hi + step / 1000; v += step) ticks.push(Math.round(v / step) * step);
  return { lo, hi, ticks };
}

/** Monotone cubic interpolation (Fritsch–Carlson), as AreaChart draws: never overshoots between points. */
function monotone(pts) {
  const n = pts.length;
  if (n < 2) return "";
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  const dx = [], dy = [], m = [];
  for (let i = 0; i < n - 1; i++) { dx.push(xs[i + 1] - xs[i]); dy.push(ys[i + 1] - ys[i]); m.push(dx[i] ? dy[i] / dx[i] : 0); }
  const t = [m[0]];
  for (let i = 1; i < n - 1; i++) t.push(m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2);
  t.push(m[n - 2]);
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { t[i] = 0; t[i + 1] = 0; continue; }
    const a = t[i] / m[i], b = t[i + 1] / m[i], h = Math.hypot(a, b);
    if (h > 3) { t[i] = (3 * a) / h * m[i]; t[i + 1] = (3 * b) / h * m[i]; }
  }
  let d = "M " + r1(xs[0]) + " " + r1(ys[0]);
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i];
    d += " C " + r1(xs[i] + h / 3) + " " + r1(ys[i] + (t[i] * h) / 3) + "," + r1(xs[i + 1] - h / 3) + " " + r1(ys[i + 1] - (t[i + 1] * h) / 3) + "," + r1(xs[i + 1]) + " " + r1(ys[i + 1]);
  }
  return d;
}

/** The legend over a chart of more than one series: a swatch (a bar's square, a line's dash) and the name, wrapped. */
function legend(series, W, paint, asLines) {
  if (series.length < 2) return { svg: "", height: 0 };
  const size = 11, rowH = 18;
  let x = 0, y = 0, out = "";
  series.forEach((s, i) => {
    const role = seriesRole(s, i);
    const name = fit(s.name, W - 24, size);
    const w = 20 + textW(name, size) + 14;
    if (x > 0 && x + w > W) { x = 0; y += rowH; }
    const mark = asLines
      ? '<line x1="' + x + '" x2="' + (x + 14) + '" y1="' + (y + 9) + '" y2="' + (y + 9) + '" stroke-width="2.5" stroke-linecap="round"' + (DASHES[i % DASHES.length] ? ' stroke-dasharray="' + DASHES[i % DASHES.length] + '"' : "") + paint(null, role) + "/>"
      : '<rect x="' + x + '" y="' + (y + 4) + '" width="10" height="10" rx="2"' + paint(role) + "/>";
    out += mark + '<text x="' + (x + 20) + '" y="' + (y + 13) + '" font-size="' + size + '"' + paint("axis") + ">" + esc(name) + "</text>";
    x += w;
  });
  return { svg: out, height: y + rowH + 6 };
}

const svgOpen = (W, H, chart, font) =>
  '<svg xmlns="http://www.w3.org/2000/svg" width="' + W + '" height="' + H + '" viewBox="0 0 ' + W + " " + H + '" role="img" aria-label="' + esc(chart.title) + '"' +
  (font ? ' font-family="' + esc(font) + '"' : "") + ">";

// ------------------------------------------------------------------ the kinds of chart

/**
 * Bars across (stores, products, channels…): the names on the left where they can be read, the bars from
 * zero, the value at the end of each bar when there is one series. maxRows: the rest are in the table.
 */
function barsAcross(chart, W, paint, opts) {
  const axis = chart.dims && chart.dims[0];
  const n = Math.min(chart.labels.length, opts.maxRows);
  const series = chart.series;
  const k = series.length;
  const lg = legend(series, W, paint, false);
  const size = 11;
  const labels = chart.labels.slice(0, n).map((l) => axisLabel(l, axis, false, chart.language));
  const rowH = k === 1 ? 26 : Math.max(26, 8 + k * 9);
  const barH = k === 1 ? 14 : Math.min(9, (rowH - 8) / k - 1);
  const values = [];
  series.forEach((s) => s.values.slice(0, n).forEach((v) => values.push(v)));
  const labelW = Math.min(W * 0.38, Math.max(48, ...labels.map((l) => textW(l, size))) + 10);
  const valueW = k === 1 ? Math.min(W * 0.3, Math.max(0, ...values.map((v) => textW(formatValue(v, chart.unit, "bar"), size))) + 10) : 6;
  // No axis is drawn across: the longest bar takes the room, and every bar says its value.
  const nums = values.filter((v) => v !== null && v !== undefined).map(Number);
  const lo = Math.min(0, ...nums), hi = Math.max(0, ...nums) || (lo < 0 ? 0 : 1);
  const x0 = labelW, x1 = Math.max(x0 + 40, W - valueW);
  const sx = (v) => x0 + ((v - lo) / (hi - lo)) * (x1 - x0);
  const top = lg.height;
  const H = top + n * rowH + 4;
  let body = "";
  labels.forEach((label, i) => {
    const y = top + i * rowH;
    body += '<text x="' + r1(x0 - 8) + '" y="' + r1(y + rowH / 2 + 4) + '" text-anchor="end" font-size="' + size + '"' + paint("text") + ">" + esc(fit(label, labelW - 12, size)) + "</text>";
    const groupH = k * barH + (k - 1) * 2;
    series.forEach((s, j) => {
      const v = s.values[i];
      if (v === null || v === undefined) return;
      const a = sx(Math.max(lo, Math.min(0, v))), b = sx(Math.max(0, v));
      const by = y + (rowH - groupH) / 2 + j * (barH + 2);
      const tip = axisLabel(chart.labels[i], axis, true, chart.language) + (k > 1 ? " · " + s.name : "") + ": " + formatValue(v, chart.unit);
      body += '<rect x="' + r1(a) + '" y="' + r1(by) + '" width="' + r1(Math.max(v === 0 ? 0 : 1.5, b - a)) + '" height="' + r1(barH) + '" rx="' + (barH > 8 ? 3 : 2) + '"' + paint(seriesRole(s, j)) + "><title>" + esc(tip) + "</title></rect>";
      if (k === 1) {
        const end = v < 0 ? a - 6 : b + 6;
        body += '<text x="' + r1(end) + '" y="' + r1(by + barH / 2 + 4) + '" text-anchor="' + (v < 0 ? "end" : "start") + '" font-size="' + size + '" font-weight="600"' + paint("text") + ">" + esc(formatValue(v, chart.unit, "bar")) + "</text>";
      }
    });
  });
  if (lo < 0) body += '<line x1="' + r1(sx(0)) + '" x2="' + r1(sx(0)) + '" y1="' + top + '" y2="' + (H - 4) + '" stroke-width="1"' + paint(null, "zero") + "/>";
  return svgOpen(W, H, chart, opts.font) + lg.svg + body + "</svg>";
}

/** The frame of a chart along time: the value axis on the left (three quiet lines), the dates under it. */
function timeFrame(chart, W, H, paint, top, values, right = 6) {
  const size = 11;
  const s = scaleOf(values, 3, chart.unit);
  const yLabelW = Math.max(...s.ticks.map((t) => textW(formatValue(t, chart.unit, "axis"), size))) + 8;
  const x0 = yLabelW + 4, x1 = W - right, y0 = top + 8, y1 = H - 22;
  const sy = (v) => y1 - ((v - s.lo) / (s.hi - s.lo)) * (y1 - y0);
  let svg = "";
  s.ticks.forEach((t) => {
    const y = r1(sy(t));
    svg += '<line x1="' + x0 + '" x2="' + x1 + '" y1="' + y + '" y2="' + y + '" stroke-width="1"' + paint(null, t === 0 ? "zero" : "grid") + "/>";
    svg += '<text x="' + (x0 - 6) + '" y="' + (y + 4) + '" text-anchor="end" font-size="' + size + '"' + paint("axis") + ">" + esc(formatValue(t, chart.unit, "axis")) + "</text>";
  });
  return { W, x0, x1, y0, y1, sy, svg, base: sy(Math.max(s.lo, Math.min(0, s.hi))) };
}

/** Names under the chart, one per ~56px, so a long month never runs together. */
function xLabels(chart, xs, f, paint) {
  const axis = chart.dims && chart.dims[0];
  const room = Math.max(1, Math.floor((f.x1 - f.x0) / 56));
  const every = Math.max(1, Math.ceil(xs.length / room));
  let svg = "";
  xs.forEach((x, i) => {
    if (i % every !== 0) return;
    const text = fit(axisLabel(chart.labels[i], axis, false, chart.language), 56 * every - 6, 11);
    const half = textW(text, 11) / 2;
    // the first and the last name keep inside the picture
    const anchor = x - half < 0 ? "start" : x + half > f.W ? "end" : "middle";
    const ax = anchor === "start" ? Math.max(0, x - 6) : anchor === "end" ? Math.min(f.W, x + 6) : x;
    svg += '<text x="' + r1(ax) + '" y="' + (f.y1 + 16) + '" text-anchor="' + anchor + '" font-size="11"' + paint("axis") + ">" + esc(text) + "</text>";
  });
  return svg;
}

/** Bars up, along time: one group per day (week, hour…), a bar per series. */
function barsUp(chart, W, paint, opts) {
  const n = chart.labels.length;
  const series = chart.series;
  const k = series.length;
  const lg = legend(series, W, paint, false);
  const H = lg.height + opts.height;
  const values = [];
  series.forEach((s) => s.values.forEach((v) => values.push(v)));
  const f = timeFrame(chart, W, H, paint, lg.height, values);
  const band = (f.x1 - f.x0) / n;
  const groupW = Math.max(1, band * (n > 20 ? 0.8 : 0.66));
  const barW = groupW / k;
  const axis = chart.dims && chart.dims[0];
  const xs = [];
  let body = "";
  for (let i = 0; i < n; i++) {
    const cx = f.x0 + band * (i + 0.5);
    xs.push(cx);
    series.forEach((s, j) => {
      const v = s.values[i];
      if (v === null || v === undefined) return;
      const y = f.sy(Math.max(0, v)), y2 = f.sy(Math.min(0, v));
      const tip = axisLabel(chart.labels[i], axis, true, chart.language) + (k > 1 ? " · " + s.name : "") + ": " + formatValue(v, chart.unit);
      body += '<rect x="' + r1(cx - groupW / 2 + j * barW) + '" y="' + r1(y) + '" width="' + r1(Math.max(1, barW - (k > 1 ? 1 : 0))) + '" height="' + r1(Math.max(v === 0 ? 0 : 1, y2 - y)) + '" rx="' + (barW > 8 ? 2 : 0) + '"' + paint(seriesRole(s, j)) + "><title>" + esc(tip) + "</title></rect>";
    });
  }
  return svgOpen(W, H, chart, opts.font) + lg.svg + f.svg + body + xLabels(chart, xs, f, paint) + "</svg>";
}

/** Lines: one per series, gaps where a value is missing; one series is filled under its line and says its last value. */
function lines(chart, W, paint, opts) {
  const n = chart.labels.length;
  const series = chart.series;
  const k = series.length;
  const lg = legend(series, W, paint, true);
  const H = lg.height + opts.height;
  const values = [];
  series.forEach((s) => s.values.forEach((v) => values.push(v)));
  // One series says its last value beside its last point, in room kept for it on the right.
  let last = -1;
  if (k === 1) series[0].values.forEach((v, i) => { if (v !== null && v !== undefined) last = i; });
  const lastText = last >= 0 ? formatValue(series[0].values[last], chart.unit, "bar") : "";
  const f = timeFrame(chart, W, H, paint, lg.height, values, lastText ? textW(lastText, 12) + 14 : 6);
  const inset = n > 1 ? 6 : 0;
  const xs = chart.labels.map((_, i) => (n === 1 ? (f.x0 + f.x1) / 2 : f.x0 + inset + (i / (n - 1)) * (f.x1 - f.x0 - 2 * inset)));
  const axis = chart.dims && chart.dims[0];
  let body = "";
  series.forEach((s, j) => {
    const role = seriesRole(s, j);
    // Runs of points with a value: a missing day breaks the line instead of drawing it at zero.
    const runs = [];
    let run = [];
    s.values.forEach((v, i) => {
      if (v === null || v === undefined) { if (run.length) runs.push(run); run = []; return; }
      run.push([xs[i], f.sy(Number(v)), i]);
    });
    if (run.length) runs.push(run);
    runs.forEach((pts) => {
      if (pts.length > 1) {
        const d = monotone(pts);
        if (k === 1) body += '<path d="' + d + " L " + r1(pts[pts.length - 1][0]) + " " + r1(f.base) + " L " + r1(pts[0][0]) + " " + r1(f.base) + ' Z" fill-opacity="0.14"' + paint(role) + "/>";
        body += '<path d="' + d + '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"' + (DASHES[j % DASHES.length] ? ' stroke-dasharray="' + DASHES[j % DASHES.length] + '"' : "") + paint(null, role) + "/>";
      }
    });
    // Every point while they fit; on a long line only the ones standing alone, which have no line to show them.
    runs.forEach((pts) => pts.forEach((p) => {
      if (n > 31 && pts.length > 1) return;
      const tip = axisLabel(chart.labels[p[2]], axis, true, chart.language) + (k > 1 ? " · " + s.name : "") + ": " + formatValue(s.values[p[2]], chart.unit);
      body += '<circle cx="' + r1(p[0]) + '" cy="' + r1(p[1]) + '" r="' + (n <= 31 ? 3 : 2) + '" stroke-width="1.5"' + paint(role, "bg") + "><title>" + esc(tip) + "</title></circle>";
    }));
  });
  if (lastText) {
    const y = Math.min(f.y1, Math.max(f.y0 + 4, f.sy(Number(series[0].values[last])) + 4));
    body += '<text x="' + r1(xs[last] + 8) + '" y="' + r1(y) + '" font-size="12" font-weight="600"' + paint("text") + ">" + esc(lastText) + "</text>";
  }
  return svgOpen(W, H, chart, opts.font) + lg.svg + f.svg + body + xLabels(chart, xs, f, paint) + "</svg>";
}

/** Shares of a whole: the largest five and the rest together, the total in the middle, a legend with value and share. */
function donut(chart, W, paint, opts) {
  const axis = chart.dims && chart.dims[0];
  const s = chart.series[0];
  let items = chart.labels.map((l, i) => ({ label: axisLabel(l, axis, false, chart.language), value: Math.max(0, Number(s.values[i]) || 0), role: SERIES[i % SERIES.length] }));
  if (items.length > 6) {
    const sorted = items.slice().sort((a, b) => b.value - a.value);
    const rest = sorted.slice(5);
    items = sorted.slice(0, 5).map((it, i) => ({ ...it, role: SERIES[i] }));
    items.push({ label: "Other (" + rest.length + ")", value: rest.reduce((t, it) => t + it.value, 0), role: "other" });
  }
  const total = items.reduce((t, it) => t + it.value, 0);
  const size = 11;
  const wide = W >= 400;
  const D = wide ? Math.min(180, W * 0.4) : Math.min(170, W - 40);
  const R = D / 2, r = R * 0.62;
  const cx = wide ? R + 4 : W / 2, cy = R + 4;
  let body = "";
  let a0 = -Math.PI / 2;
  const pt = (rad, a) => r1(cx + rad * Math.cos(a)) + " " + r1(cy + rad * Math.sin(a));
  items.forEach((it) => {
    if (!it.value || !total) return;
    const tip = it.label + ": " + formatValue(it.value, chart.unit) + " (" + trimZero(((it.value / total) * 100).toFixed(1)) + "%)";
    if (it.value >= total) {   // one slice is the whole ring
      body += '<path d="M ' + pt(R, a0) + " A " + R + " " + R + " 0 1 1 " + pt(R, a0 + Math.PI) + " A " + R + " " + R + " 0 1 1 " + pt(R, a0) + " M " + pt(r, a0) + " A " + r + " " + r + " 0 1 0 " + pt(r, a0 + Math.PI) + " A " + r + " " + r + " 0 1 0 " + pt(r, a0) + ' Z" fill-rule="evenodd"' + paint(it.role) + "><title>" + esc(tip) + "</title></path>";
      return;
    }
    const a1 = a0 + (it.value / total) * Math.PI * 2;
    const big = a1 - a0 > Math.PI ? 1 : 0;
    body += '<path d="M ' + pt(R, a0) + " A " + R + " " + R + " 0 " + big + " 1 " + pt(R, a1) + " L " + pt(r, a1) + " A " + r + " " + r + " 0 " + big + " 0 " + pt(r, a0) + ' Z" stroke-width="1.5"' + paint(it.role, "bg") + "><title>" + esc(tip) + "</title></path>";
    a0 = a1;
  });
  body += '<text x="' + r1(cx) + '" y="' + r1(cy + 2) + '" text-anchor="middle" font-size="15" font-weight="700"' + paint("text") + ">" + esc(formatValue(total, chart.unit, "axis")) + "</text>";
  body += '<text x="' + r1(cx) + '" y="' + r1(cy + 18) + '" text-anchor="middle" font-size="10"' + paint("axis") + ">total</text>";

  const lx = wide ? D + 28 : 0, ly = wide ? Math.max(4, cy - (items.length * 22) / 2) : D + 18;
  const lw = Math.min(W - lx, 340);
  const lr = lx + lw;   // the legend's right edge: values and shares line up on it
  items.forEach((it, i) => {
    const y = ly + i * 22;
    const share = total ? trimZero(((it.value / total) * 100).toFixed(1)) + "%" : "–";
    const value = formatValue(it.value, chart.unit, "bar");
    const right = 52 + textW(value, size) + 12;
    body += '<rect x="' + lx + '" y="' + (y + 4) + '" width="10" height="10" rx="2"' + paint(it.role) + "/>";
    body += '<text x="' + (lx + 18) + '" y="' + (y + 13) + '" font-size="' + size + '"' + paint("text") + ">" + esc(fit(it.label, lw - 18 - right, size)) + "</text>";
    body += '<text x="' + r1(lr - 52) + '" y="' + (y + 13) + '" text-anchor="end" font-size="' + size + '" font-weight="600"' + paint("text") + ">" + esc(value) + "</text>";
    body += '<text x="' + r1(lr - 2) + '" y="' + (y + 13) + '" text-anchor="end" font-size="' + size + '"' + paint("axis") + ">" + esc(share) + "</text>";
  });
  const H = wide ? Math.max(D + 8, ly + items.length * 22 + 4) : ly + items.length * 22 + 4;
  return svgOpen(W, H, chart, opts.font) + body + "</svg>";
}

// ------------------------------------------------------------------ the one way in

/**
 * The chart as an SVG string, width W px (or pt, in the PDF). opts: palette (PRINT_PALETTE for the PDF; none
 * in the chat), font (the PDF's font name), maxRows (bars across: the rest stay in the table), height (the
 * plot of a chart along time). A table has no picture: the callers draw it from chart.table.
 */
export function chartSvg(chart, W, opts = {}) {
  if (!chart || chart.type === "table" || !hasData(chart)) return "";
  const o = { palette: null, font: "", maxRows: 12, height: 200, ...opts };
  const paint = painter(o.palette);
  const W0 = Math.max(200, Math.round(W));
  const axis = chart.dims && chart.dims[0];
  if (chart.type === "donut") return donut(chart, W0, paint, o);
  if (chart.type === "line") return lines(chart, W0, paint, o);
  return TIME_AXES.indexOf(axis) !== -1 ? barsUp(chart, W0, paint, o) : barsAcross(chart, W0, paint, o);
}

/** How many rows bars across would leave for the table (the chat's "Show all"). */
export function hiddenRows(chart, maxRows) {
  const axis = chart && chart.dims && chart.dims[0];
  if (!chart || chart.type !== "bar" || TIME_AXES.indexOf(axis) !== -1) return 0;
  return Math.max(0, chart.labels.length - maxRows);
}
